import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { UserSearchType } from 'models/restTypes';
import { SystemVariant } from 'models/systems';
import { rootStore } from 'stores/contexts/storesContext';

const BASE_URL = process.env.REACT_APP_BASE_URL;
const AUTH_URL = process.env.REACT_APP_BASE_URL_AUTH;

export const ApiEndpoints = {
  postLogin: (email: string, password: string) =>
    `/public/users/login?email=${email}&password=${password}`,
  postForgotPassword: (email: string) =>
    `/public/users/password/forgot?email=${email}&linkTemplate=${process.env.REACT_APP_FORGOT_PASS_TEMP_LINK}`,
  postResetPassword: (
    newPassword: string,
    email: string,
    passwordToken: string,
  ) =>
    `/public/users/password/reset?email=${email}&passwordToken=${passwordToken}&newPassword=${newPassword}`,
  postAccessOtc: (phoneNumber: string) =>
    `/public/access-otc/backoffice?phone=${phoneNumber}`,
  postVerifyOtc: (requestId: string, otp: string) =>
    `/public/verify-otc?requestId=${requestId}&otp=${otp}`,
  postLogout: () => `/public/users/logout`, // TBD: call function on logout
  postRefreshToken: (refreshToken: string) =>
    `/public/users/renew-access-token?refresh_token=${refreshToken}`,
  getSupportedLocales: () => '/public/supported-locales',
  getCropProtocol: (protocolId: string) => `/api/v3/protocols/${protocolId}`,
  getAllProtocols: () => `/api/v3/protocols`,
  postUploadExcelProtocols: () => `/api/v3/protocols/model`,
  postUploadJSONProtocols: () => `/api/v3/protocols/model`,
  getGrowers: () => '/api/v3/protocols/growers',
  getCropTypes: () => '/api/v3/crop-types',
  getPlots: () => '/api/v3/protocols/plots',
  getGrowerPlots: (growerId: number, historic: boolean) =>
    `/api/v3/growers/${growerId}/plots?historic=${historic}`,
  getGrowerSystems: (growerId: number) => `/api/v3/growers/${growerId}/systems`,
  getSystemTypes: () => '/api/v3/system-types',
  getSensorTypes: () => '/api/v3/sensor-types',
  postCreateSystem: () => '/api/v3/growers/systems',
  deleteSystem: (systemId: number) => `/api/v3/systems/${systemId}`,
  putUpdateSystem: (systemId: number) => `/api/v3/systems/${systemId}`,
  postBatchCreateSensors: (growerId: number, systemId: number) =>
    `/api/v3/growers/${growerId}/systems/${systemId}/sensors/batch`,
  postCreateSensor: (growerId: number, sensorQuery: string) =>
    `/api/v3/growers/${growerId}/sensors${sensorQuery}`,
  putUpdateSensor: () => `/api/v3/sensors`,
  // Requires a body with array of sensor ids
  putActivateSensor: (isActive: boolean) =>
    `/api/v3/sensors/activate/${isActive}`,
  deleteSensor: (growerId: number, systemId: number, sensorId: number) =>
    `/api/v3/growers/${growerId}/systems/${systemId}/sensors/${sensorId}/remove`,
  putAttachMultiplePlotToSensor: (plotId: number) =>
    `/api/v3/plots/${plotId}/sensors`,
  deleteSensorsFromPlot: (plotId: number, sensorId: number) =>
    `/api/v3/plots/${plotId}/sensors/${sensorId}`,
  getSystemActiveSensors: (growerId: number, systemId: number) =>
    `/api/v3/growers/${growerId}/systems/${systemId}/sensors/active`,
  getSystemSensors: (growerId: number, systemId: number) =>
    `/api/v3/growers/${growerId}/systems/${systemId}/sensors`,
  getPlotSensors: (plotId: number) => `/api/v3/plots/${plotId}/systems-sensors`,
  getSystemValves: (systemId: number) => `/api/v3/systems/${systemId}/valves`,
  getGrowerSensors: (growerId: number) => `/api/v3/growers/${growerId}/sensors`,
  getSystemConfigurationSchema: (systemType: SystemVariant) =>
    `/api/v3/system-types/${systemType}`,
  getPlotFilters: (protocolId: string) =>
    `/api/v3/protocols/${protocolId}/plot-stats`,
  getPlotTemplate: () => '/api/v3/growers/plots/batch-template-file',
  putDisableUsers: () => '/api/v3/disable',
  putEnableUsers: () => '/api/v3/enable',
  deleteCropProtocol: (growerId: number, protocolId: string) =>
    `/api/v3/growers/${growerId}/protocols/${protocolId}`,
  // Users
  getAllUsers: () => '/api/v3/users',
  postUser: () => '/api/v3/users',
  putUsers: () => '/api/v3/users',
  getUser: (userId: number) => `/api/v3/users/${userId}/active`,
  putUnsubscribeUser: (userId: number) => `/api/v3/users/${userId}/unsubscribe`,
  resetUsersPreferences: () => 'api/v3/users/preferences/reset',
  deleteUsers: () => '/api/v3/users',
  getUserPreferences: () => '/api/v3/user-preferences/default',
  getAllCompanyRoles: () => '/api/v3/company-roles',
  postUploadUsersExcel: () => '/api/v3/users/parse-excel',
  postCreateUsers: () => '/api/v3/users/upload-batch',
  getUsersExcelTemplate: () => '/api/v3/users/excel-template',
  // UserToGrower Relationship
  getUserGrowers: (
    userId: number,
    offset: number,
    cnt: number,
    isAssigned: boolean,
  ) =>
    `/api/v3/users/${userId}/growers?offset=${offset}&cnt=${cnt}&isAssigned=${isAssigned}`,
  getUserGrowersRoles: (userId: number) =>
    `/api/v3/users/${userId}/growers/roles`,
  putUserGrowersRole: (userId: number, roleId: number) =>
    `/api/v3/users/${userId}/growers/roles/${roleId}`,
  deleteUnassignUserGrowers: (userId: number) =>
    `/api/v3/users/${userId}/growers/assignment`,
  putAssignUserGrowers: (userId: number, roleId: number) =>
    `/api/v3/growers/users/${userId}/roles/${roleId}/assignment`,
  // Reseller Page
  getAllGrowers: () => `/api/v3/growers`,
  postGrower: () => `/api/v3/growers`,
  putGrowers: () => '/api/v3/growers',
  deleteGrowers: () => '/api/v3/growers',
  putUpdateGrowerStatus: (growerId: number, status: boolean) =>
    `/api/v3/growers/${growerId}?active=${status}`,
  getResellerUsers: (growerId: number) => `/api/v3/growers/${growerId}/users`,
  getUserNotification: (userId: number) =>
    `/api/v3/reseller/userNotification?userId=${userId}`,
  putMoveGrowerToReseller: (resellerId: number | string) =>
    `/api/v3/growers/move?resellerId=${resellerId}`,
  getResellerPlots: (resellerId: number, historic: boolean) =>
    `/api/v3/growers/${resellerId}/plots?historic=${historic}`,
  deleteResellerPlot: (resellerId: number, plotId: number) =>
    `/api/v3/growers/${resellerId}/plots/${plotId}`,
  postUploadPlotsExcel: (growerId: number | null) =>
    `/api/v3/growers/${growerId}/plots/excel`,
  postCreatePlotsFromJson: (growerId: number | null) =>
    `/api/v3/growers/${growerId}/plots/json`,
  // Create Plot
  putUpdatePlotsStatus: (growerId: number | null) =>
    `/api/v3/growers/${growerId}/plots/status`,
  getGrowingTypes: () => '/api/v3/grow-types',
  getSoilTypes: () => '/api/v3/soil-types',
  getServiceLevels: () => '/api/v3/service-levels',
  getIrrigationMethods: () => '/api/v3/plots/irrigation-method/types',
  getPlotProfiles: () => '/api/v3/plot-profiles',
  getGrowerCropTypes: (growerId: number) =>
    `/api/v3/growers/${growerId}/crop-types?visitAncestors=true`,
  getCropCategories: (growerId: number | null, cropId: number | null) =>
    `/api/v3/grower/${growerId}/crops/${cropId}/categories`,
  getCropVarieties: (growerId: number | null, cropId: number | null) =>
    `/api/v3/growers/${growerId}/crops/${cropId}/varieties`,
  getCropProtocolId: (
    growerId: number | null,
    cropId: number | null,
    latitude: number,
    longitude: number,
    variety: string,
    category: string,
  ) => {
    const varietyParam = variety ? `&cropVariety=${variety}` : '';
    const categoryParam = category ? `&cropCategory=${category}` : '';
    return `/api/v3/growers/${growerId}/crops/${cropId}/protocols?latitude=${latitude}&longitude=${longitude}&idOnly=true${varietyParam}${categoryParam}`;
  },
  postCreatePlot: (growerId: number | null) =>
    `/api/v3/growers/${growerId}/plots`,
  getPhenologicalStages: (
    protocolId: string | null,
    plantDate: number | null,
    plotId: number | null,
  ) =>
    `/api/v3/protocols/${protocolId}/phenological-stages?plantDate=${plantDate}${plotId ? `&plotId=${plotId}` : ''
    }`,
  getGrowingMethods: (
    protocolId: string | null,
    plantDate: number | null,
    plotId: number | null,
  ) =>
    `/api/v3/protocols/${protocolId}/grow-types?plantDate=${plantDate}${plotId ? `&plotId=${plotId}` : ''
    }`,
  putUpdatePlot: (growerId: number | null, plotId: number | null) =>
    `/api/v3/growers/${growerId}/plots/${plotId}`,
  // AI Features
  getFeaturesSchema: () => '/api/v3/mlmodels',
  postFeature: (plotId: number | null, featureType: string) =>
    `/api/v3/mlmodels/plots/${plotId}/mlsensors/activation?modelType=${featureType}`,
  getFeaturesByPlot: (plotId: number, modelType: string) =>
    `/api/v3/mlmodels/plots/${plotId}/mlsensors?modelType=${modelType}`,
  getFeaturesByGrower: (growerId: number) =>
    `/api/v3/mlmodels/growers/${growerId}/mlsensors`,
  deleteFeature: (plotId: number | null, featureId: number | null) =>
    `/api/v3/mlmodels/plots/${plotId}/mlsensors/${featureId}`,
  getRunAnomalyDetection: (growerId: string | number) =>
    `api/v3/run/anomaly_detection?growerId=${growerId}`,
  // Plot settings features
  getPlotParams: (plotId: string | number) => `api/v3/plots/${plotId}/params`,
  postPlotParams: (plotId: string | number, comment: string) =>
    `api/v3/plots/${plotId}/params?comment=${comment}`,
  resetWaterConsumption: (plotId: string | number, growerId: string | number) =>
    `api/v2/grower/${growerId}/plot/${plotId}/measurement/resetWaterConsumption`,
  getCoupons: () => `/api/v3/coupon`,
  getSystems: () => `/api/v3/systems`,
  getSerialSystem: (type: string, serial: string | undefined) =>
    `/api/v3/systems/${type}${serial ? `?serial=${serial}` : ''}`,
  getPlotRecommendationValidation: () =>
    `/api/v3/schema/recommendation-params/validation`,
  getPlotRecommendationParams: (plotId: number | string) =>
    `/api/v3/plots/${plotId}/recommendation-params`,
  postRecommendationParams: (plotId: number | string) =>
    `/api/v3/plots/${plotId}/recommendation-params`,
  getPlotGbiParams: (plotId: number | string) =>
    `/api/v3/plots/${plotId}/irrigation-params`,
  postPlotGbiParams: (plotId: number | string, comment: string) =>
    `/api/v3/plots/${plotId}/irrigation-params?comment=${comment}`,
  postSendWANotification: (growerId: string | number) =>
    `/api/v3/events/dispatch?growerId=${growerId}`,
  getResellerSearchTree: (searchText: string) =>
    `/api/v3/growers/search?searchText=${searchText}`,
  getApiKeys: () => `/api/v1/keys`,
  getActions: (growerId: number) => `/api/v2/grower/${growerId}/action`,
  getActionParams: (actionId: number | undefined, systemId: number, systemType: string) =>
    `/api/v2/actionParams/type/${systemType}?systemId=${systemId}${actionId ? `&actionId=${actionId}` : ''}`,
  deleteAction: (systemId: number, actionId: number) => `/api/v3/systems/${systemId}/actions/${actionId}`,
  postAction: () => `/api/v3/actions`,
  putAction: (actionId: number, systemId: number) => `/api/v3/systems/${systemId}/actions/${actionId}`,
  putUserProfile: () => `/api/v3/users/profile`,
  getIWSystems: (iwOrderId: string) => `/api/v3/growers/by-iw-order?iwOrderId=${iwOrderId}`,
  getNotificationTypes: () => `/api/v3/notification-config/notification-types`,
  getUserProfiles: () => `/api/v3/notification-config/user-profiles`,
  getNotificationTemplates: () => `/api/v3/notification-config/template-configs`,
  postS3TemplateImage: () => `/api/v3/notification-config/plant/template-images`,
  putTemplate: () => `/api/v3/notification-config`,
  postTestTemplateNotification: (notificationTypeId: number | string, templateName: number | string, templateLanguage: string) =>
    `/api/v3/notification-config/test/produce-event?notificationTypeId=${notificationTypeId}&templateName=${templateName}&templateLanguage=${templateLanguage}`,
  getCampaigns: () => `/api/v3/campaign`,
  postCampaign: () => `/api/v3/campaign`,
  getAgents: () => `/api/v3/agents`,
  deleteCampaign: (campaignUuid: number | string) => `/api/v3/campaign?uuid=${campaignUuid}`,
  postCampaignS3Image: () => `/api/v3/campaign/template-images`,
  getVpdThresholds: () => `/api/v3/plot-default-params-by-crop`,
  getSearchUsers: (query: string, searchType: UserSearchType) =>
    `/api/v3/users/search?query=${query}&searchType=${searchType}`,
  getSchemaValidation: (entityCode: string) =>
    `/api/v3/schema/${entityCode}/validation`,
  deleteTrigger: (triggerUid: number) => `/api/v3/triggers/${triggerUid}`,
  postTrigger: (plotId: number) => `/api/v3/plots/${plotId}/triggers`,
  getTriggers: (plotId: number) => `/api/v3/plots/${plotId}/triggers`,
  getGrowerActions: (growerId: number, plotId: number) =>
    `/api/v3/growers/${growerId}/plots/${plotId}/actions`,
  getActionsExcludingPlot: (growerId: number, plotId: number) =>
    `/api/v2/grower/${growerId}/action?excludePlotId=${plotId}`,
  plotActionAttachment: (growerId: number, plotId: number, actionId: number) =>
    `/api/v3/growers/${growerId}/plots/${plotId}/actions/${actionId}/attachment`,
};

const Api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const ApiPublic = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const AuthApi = axios.create({
  baseURL: AUTH_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

Api.interceptors.response.use(
  (response) => {
    // Do something with response data
    return response;
  },
  async (error: Error | AxiosError) => {
    if (axios.isAxiosError(error)) {
      if ((error as AxiosError).response?.status === 401) {
        try {
          if (!rootStore.userStore.isRefreshingToken) {
            await rootStore.userStore.refreshUserToken();
          } else {
            await rootStore.userStore.isRefreshingToken;
          }

          if (rootStore.userStore.accessTokenBlock?.access_token) {
            const axiosRequestConfig = error.config as AxiosRequestConfig;
            axiosRequestConfig.headers = {
              ...axiosRequestConfig.headers,
              Authorization: `Bearer ${rootStore.userStore.accessTokenBlock?.access_token}`,
            };

            return await axios.request(axiosRequestConfig);
          }
        } catch (e) {
          if (axios.isAxiosError(e) && e.response?.status === 401) {
            rootStore.userStore.logOut();
          } else {
            return Promise.reject(e);
          }
        }
      }
    }

    return Promise.reject(error);
  },
);

export const setAuthorizationHeader = (token?: string) => {
  Api.interceptors.request.use((config) => {
    if (config && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  });
};

export default Api;
export { AuthApi, ApiPublic };
